class ProductInfoTabs {
  constructor(section) {
    this.section = section;
    this.tabButtons = section.querySelectorAll('[data-tab-trigger]');
    this.tabPanels = section.querySelectorAll('[data-tab-panel]');
    this.accordionItems = section.querySelectorAll('[data-accordion-item]');

    this.init();
  }

  init() {
    if (!this.section || this.section.dataset.initialized === 'true') return;

    this.initTabs();
    this.initAccordions();
    this.normalizeAccordionInitialState();
    this.lockVisibleAccordions();
    this.initReviews();
    this.initProductTabsFaq();
    this.initFaqMobileToggle();
    this.initResizeHandler();
    this.initFontLoadHandler();

    this.section.dataset.initialized = 'true';
  }

  initTabs() {
    this.tabButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        this.switchTab(e.currentTarget);
      });
    });

    const tablist = this.section.querySelector('[role="tablist"]');
    if (tablist) {
      tablist.addEventListener('keydown', (e) => {
        this.handleTabKeyboard(e);
      });
    }
  }

  switchTab(activeButton) {
    const targetPanelId = activeButton.dataset.tabTarget;
    const targetPanel = this.section.querySelector(`#${targetPanelId}`);

    if (!targetPanel) return;

    this.tabButtons.forEach(btn => {
      btn.classList.remove('is-active');
      btn.setAttribute('aria-selected', 'false');
      btn.setAttribute('tabindex', '-1');
    });

    activeButton.classList.add('is-active');
    activeButton.setAttribute('aria-selected', 'true');
    activeButton.setAttribute('tabindex', '0');

    this.tabPanels.forEach(panel => {
      panel.setAttribute('hidden', '');
    });

    targetPanel.removeAttribute('hidden');

    requestAnimationFrame(() => {
      this.lockVisibleAccordions();

      if (targetPanel.hasAttribute('data-reviews-panel') && this.isMobileReviews()) {
        this.initMobileReviewDots();
      }

      if (targetPanel.hasAttribute('data-product-tabs-faq')) {
        this.refreshProductTabsFaq();
      }
    });
  }

  handleTabKeyboard(e) {
    const tabs = Array.from(this.tabButtons);
    const currentIndex = tabs.indexOf(document.activeElement);

    if (currentIndex === -1) return;

    let targetIndex = currentIndex;

    switch(e.key) {
      case 'ArrowRight':
        e.preventDefault();
        targetIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        e.preventDefault();
        targetIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        e.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        targetIndex = tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.switchTab(document.activeElement);
        return;
      default:
        return;
    }

    tabs[targetIndex].focus();
  }

  initAccordions() {
    this.accordionItems.forEach(item => {
      const trigger = item.querySelector('[data-accordion-trigger]');
      const body = item.querySelector('.product-info-tabs__accordion-body');

      if (!trigger || !body) return;

      trigger.addEventListener('click', () => {
        this.toggleAccordion(item, trigger, body);
      });

      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleAccordion(item, trigger, body);
        }
      });
    });
  }

  toggleAccordion(item, trigger, body) {
    const isOpen = item.classList.contains('is-open');

    if (isOpen) {
      this.closeAccordion(item, trigger, body);
      return;
    }

    const accordionGroup = item.closest('.product-info-tabs__accordion');

    if (accordionGroup) {
      const openItems = accordionGroup.querySelectorAll('.product-info-tabs__accordion-item.is-open');

      openItems.forEach((openItem) => {
        if (openItem === item) return;

        const openTrigger = openItem.querySelector('[data-accordion-trigger]');
        const openBody = openItem.querySelector('.product-info-tabs__accordion-body');

        if (openTrigger && openBody) {
          this.closeAccordion(openItem, openTrigger, openBody);
        }
      });

      if (!accordionGroup.style.minHeight || accordionGroup.style.minHeight === '') {
        this.lockAccordionGroupHeight(accordionGroup);
      }
    }

    this.openAccordion(item, trigger, body);
  }

  openAccordion(item, trigger, body) {
    item.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    body.setAttribute('aria-hidden', 'false');

    body.style.height = '0px';

    requestAnimationFrame(() => {
      const targetHeight = body.scrollHeight;
      body.style.height = `${targetHeight}px`;
    });

    const onTransitionEnd = (event) => {
      if (event.propertyName !== 'height') return;
      body.style.height = 'auto';
      body.removeEventListener('transitionend', onTransitionEnd);
    };

    body.addEventListener('transitionend', onTransitionEnd);
  }

  closeAccordion(item, trigger, body) {
    const currentHeight = body.scrollHeight;

    body.style.height = `${currentHeight}px`;

    requestAnimationFrame(() => {
      body.style.height = '0px';
      item.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      body.setAttribute('aria-hidden', 'true');
    });
  }

  normalizeAccordionInitialState() {
    const accordionGroups = this.section.querySelectorAll('.product-info-tabs__accordion');

    accordionGroups.forEach((group) => {
      const openItems = Array.from(group.querySelectorAll('.product-info-tabs__accordion-item.is-open'));

      if (openItems.length > 1) {
        openItems.slice(1).forEach((item) => {
          const trigger = item.querySelector('[data-accordion-trigger]');
          const body = item.querySelector('.product-info-tabs__accordion-body');

          if (trigger && body) {
            item.classList.remove('is-open');
            trigger.setAttribute('aria-expanded', 'false');
            body.setAttribute('aria-hidden', 'true');
            body.style.height = '0px';
          }
        });
      }
    });
  }

  lockVisibleAccordions() {
    if (window.innerWidth < 768) {
      const accordionGroups = this.section.querySelectorAll('.product-info-tabs__accordion');
      accordionGroups.forEach((group) => {
        group.style.minHeight = '';
      });
      return;
    }

    const visiblePanels = Array.from(this.tabPanels).filter(panel => !panel.hasAttribute('hidden'));

    visiblePanels.forEach((panel) => {
      const accordionGroups = panel.querySelectorAll('.product-info-tabs__accordion');
      accordionGroups.forEach((group) => {
        this.lockAccordionGroupHeight(group);
      });
    });
  }

  lockAccordionGroupHeight(accordionGroup) {
    if (!accordionGroup) return;

    const ACCORDION_BODY_OPEN_PADDING_TOP = 4;

    const items = Array.from(accordionGroup.querySelectorAll('.product-info-tabs__accordion-item'));
    if (items.length === 0) return;

    let maxBodyHeight = 0;
    items.forEach((item) => {
      const body = item.querySelector('.product-info-tabs__accordion-body');
      if (body) {
        const bodyHeight = body.scrollHeight + ACCORDION_BODY_OPEN_PADDING_TOP;
        if (bodyHeight > maxBodyHeight) {
          maxBodyHeight = bodyHeight;
        }
      }
    });

    const openItem = accordionGroup.querySelector('.product-info-tabs__accordion-item.is-open');
    let currentOpenHeight = 0;

    if (openItem) {
      const openBody = openItem.querySelector('.product-info-tabs__accordion-body');
      if (openBody) {
        const currentHeight = openBody.style.height;
        if (currentHeight === 'auto') {
          currentOpenHeight = openBody.scrollHeight;
        } else {
          currentOpenHeight = parseFloat(currentHeight) || 0;
        }
      }
    }

    const currentGroupHeight = accordionGroup.offsetHeight;
    const targetMinHeight = currentGroupHeight - currentOpenHeight + maxBodyHeight;

    accordionGroup.style.minHeight = `${targetMinHeight}px`;
  }

  initReviews() {
    this.reviewsPanel = this.section.querySelector('[data-reviews-panel]');
    if (!this.reviewsPanel) return;

    this.reviewsTrack = this.reviewsPanel.querySelector('[data-reviews-track]');
    this.reviewCards = Array.from(this.reviewsPanel.querySelectorAll('[data-review-card]'));
    this.reviewsDotsContainer = this.reviewsPanel.querySelector('[data-reviews-dots]');
    this.reviewsObserver = null;
    this.reviewsLastBreakpoint = this.getReviewsBreakpoint();

    if (this.reviewCards.length === 0) return;

    this.applyReviewVisibility();

    if (this.isMobileReviews()) {
      this.initMobileReviewDots();
    }
  }

  getReviewsBreakpoint() {
    const w = window.innerWidth;
    if (w >= 1025) return 'desktop';
    if (w >= 768) return 'tablet';
    return 'mobile';
  }

  isMobileReviews() {
    return window.innerWidth < 768;
  }

  /**
   * TL14: the PDP now renders one row of four and nothing else, so every card that exists is
   * meant to be on screen at every width.
   *
   * This used to return 3 at tablet (768-1024px). With the Load More control removed, that
   * would have hidden the fourth card with nothing left on the page able to reveal it -- the
   * acceptance only measures 1440 and 390, where the old numbers happened to show all four,
   * so the stranded card would not have been caught there.
   *
   * getReviewsBatchSize() went with it: it sized the Load More batches and had no caller.
   */
  getInitialReviewsVisibleCount() {
    return this.reviewCards.length;
  }

  applyReviewVisibility() {
    if (!this.reviewCards) return;

    // Kept (rather than deleted) because handleReviewsResize() still calls it: it guarantees no
    // card is left carrying a stale is-hidden/animation class across a breakpoint change.
    this.reviewCards.forEach((card) => {
      card.classList.remove('is-hidden', 'is-entering', 'is-visible', 'is-leaving');
    });
  }

  initMobileReviewDots() {
    if (!this.reviewsDotsContainer || !this.reviewsTrack) return;
    if (!this.isMobileReviews()) return;

    this.destroyMobileReviewDots();

    const visibleCards = this.reviewCards.filter(c => !c.classList.contains('is-hidden'));
    if (visibleCards.length === 0) return;

    visibleCards.forEach((card, idx) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'product-info-tabs__reviews-dot' + (idx === 0 ? ' product-info-tabs__reviews-dot--active' : '');
      dot.setAttribute('aria-label', `Go to review ${idx + 1}`);
      dot.addEventListener('click', () => {
        card.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      });
      this.reviewsDotsContainer.appendChild(dot);
    });

    const dots = Array.from(this.reviewsDotsContainer.querySelectorAll('.product-info-tabs__reviews-dot'));

    this.reviewsObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const idx = visibleCards.indexOf(entry.target);
        if (idx === -1) return;
        dots.forEach(d => d.classList.remove('product-info-tabs__reviews-dot--active'));
        if (dots[idx]) dots[idx].classList.add('product-info-tabs__reviews-dot--active');
      });
    }, {
      root: this.reviewsTrack,
      threshold: 0.5
    });

    visibleCards.forEach(card => this.reviewsObserver.observe(card));
  }

  destroyMobileReviewDots() {
    if (this.reviewsObserver) {
      this.reviewsObserver.disconnect();
      this.reviewsObserver = null;
    }
    if (this.reviewsDotsContainer) {
      this.reviewsDotsContainer.innerHTML = '';
    }
  }

  handleReviewsResize() {
    if (!this.reviewsPanel || this.reviewCards.length === 0) return;

    const newBreakpoint = this.getReviewsBreakpoint();
    const breakpointChanged = newBreakpoint !== this.reviewsLastBreakpoint;

    if (breakpointChanged) {
      this.reviewsLastBreakpoint = newBreakpoint;
    }

    this.applyReviewVisibility();

    if (this.isMobileReviews()) {
      this.initMobileReviewDots();
    } else {
      this.destroyMobileReviewDots();
    }
  }

  initProductTabsFaq() {
    this.faqRoot = this.section.querySelector('[data-product-tabs-faq]');
    if (!this.faqRoot) return;

    this.faqItems = Array.from(this.faqRoot.querySelectorAll('[data-product-tabs-faq-item]'));
    this.faqColumns = Array.from(this.faqRoot.querySelectorAll('.product-info-tabs__faq-column'));
    this.faqActiveItem = null;
    this.faqTransitioning = false;
    this.faqMobileLock = false;

    const firstOpen = this.faqRoot.querySelector('.product-info-tabs__faq-item.is-open');
    if (firstOpen) {
      this.faqActiveItem = firstOpen;
      const fa = firstOpen.querySelector('[data-product-tabs-faq-panel]');
      if (fa && this.isFaqVisible()) {
        fa.style.height = fa.scrollHeight + 'px';
      }
    }

    this.faqItems.forEach((item) => {
      const trigger = item.querySelector('[data-product-tabs-faq-trigger]');
      if (!trigger) return;
      trigger.addEventListener('click', () => this.handleFaqClick(item));
    });

    if (this.isFaqVisible()) {
      this.lockFaqColumnHeights();
    }
  }

  isFaqVisible() {
    return this.faqRoot && !this.faqRoot.hasAttribute('hidden');
  }

  handleFaqClick(item) {
    const isMobile = window.innerWidth < 768;
    const isAlreadyOpen = item === this.faqActiveItem;

    if (!isMobile && this.faqTransitioning) return;

    if (isAlreadyOpen) {
      this.closeFaqItem(item);
      this.faqActiveItem = null;
      return;
    }

    if (isMobile) {
      if (this.faqMobileLock) return;
      if (this.faqActiveItem) {
        this.faqMobileLock = true;
        const prevItem = this.faqActiveItem;
        this.faqActiveItem = item;
        this.closeFaqMobileSeq(prevItem, () => {
          this.faqMobileLock = false;
          this.openFaqMobileSeq(item);
        });
      } else {
        this.faqActiveItem = item;
        this.openFaqMobileSeq(item);
      }
      return;
    }

    if (!this.faqActiveItem) {
      this.faqActiveItem = item;
      this.openFaqItem(item);
      return;
    }

    this.faqTransitioning = true;
    const closing = this.faqActiveItem;
    const prevAnswer = closing.querySelector('[data-product-tabs-faq-panel]');
    this.faqActiveItem = null;

    const onEnd = (e) => {
      if (e.propertyName !== 'height') return;
      prevAnswer.removeEventListener('transitionend', onEnd);
      this.faqTransitioning = false;
      this.faqActiveItem = item;
      this.openFaqItem(item);
    };

    prevAnswer.addEventListener('transitionend', onEnd);
    this.closeFaqItem(closing);
  }

  openFaqItem(item) {
    const trigger = item.querySelector('[data-product-tabs-faq-trigger]');
    const panel = item.querySelector('[data-product-tabs-faq-panel]');
    if (!trigger || !panel) return;

    item.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');
    panel.style.height = panel.scrollHeight + 'px';
  }

  closeFaqItem(item) {
    const trigger = item.querySelector('[data-product-tabs-faq-trigger]');
    const panel = item.querySelector('[data-product-tabs-faq-panel]');
    if (!trigger || !panel) return;

    panel.style.height = panel.scrollHeight + 'px';
    requestAnimationFrame(() => {
      panel.style.height = '0px';
    });
    item.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    panel.setAttribute('aria-hidden', 'true');
  }

  closeFaqMobileSeq(item, onDone) {
    const panel = item.querySelector('[data-product-tabs-faq-panel]');
    const trigger = item.querySelector('[data-product-tabs-faq-trigger]');
    if (!panel) { onDone && onDone(); return; }

    panel.style.transition = 'opacity 0.1s ease';
    panel.style.opacity = '0';
    setTimeout(() => {
      panel.style.transition = 'height 0.2s ease';
      panel.style.height = panel.scrollHeight + 'px';
      panel.offsetHeight;
      panel.style.height = '0px';
      setTimeout(() => {
        item.classList.remove('is-open');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        panel.setAttribute('aria-hidden', 'true');
        panel.style.height = '';
        panel.style.opacity = '';
        panel.style.transition = '';
        onDone && onDone();
      }, 220);
    }, 110);
  }

  openFaqMobileSeq(item) {
    const panel = item.querySelector('[data-product-tabs-faq-panel]');
    const trigger = item.querySelector('[data-product-tabs-faq-trigger]');
    if (!panel || !trigger) return;

    panel.style.opacity = '0';
    panel.style.height = '0px';
    item.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');
    panel.style.transition = 'height 0.25s ease, opacity 0.25s ease';
    panel.offsetHeight;
    panel.style.height = panel.scrollHeight + 'px';
    panel.style.opacity = '1';
    setTimeout(() => {
      panel.style.transition = '';
      panel.style.opacity = '';
    }, 260);
  }

  lockFaqColumnHeights() {
    if (!this.faqColumns || this.faqColumns.length === 0) return;
    this.faqColumns.forEach((col) => { col.style.minHeight = ''; });
    if (window.innerWidth < 768) return;
    if (!this.isFaqVisible()) return;

    this.faqColumns.forEach((col) => {
      const colItems = Array.from(col.querySelectorAll('[data-product-tabs-faq-item]'));
      let maxAnswerH = 0;
      colItems.forEach((item) => {
        const panel = item.querySelector('[data-product-tabs-faq-panel]');
        if (!panel) return;
        const h = panel.scrollHeight;
        if (h > maxAnswerH) maxAnswerH = h;
      });

      const openInCol = colItems.find((i) => i === this.faqActiveItem);
      let currentAnswerH = 0;
      if (openInCol) {
        const openPanel = openInCol.querySelector('[data-product-tabs-faq-panel]');
        if (openPanel) {
          currentAnswerH = parseFloat(openPanel.style.height) || 0;
        }
      }
      col.style.minHeight = (col.offsetHeight - currentAnswerH + maxAnswerH) + 'px';
    });
  }

  refreshProductTabsFaq() {
    if (!this.faqRoot) return;

    if (this.faqActiveItem) {
      const panel = this.faqActiveItem.querySelector('[data-product-tabs-faq-panel]');
      if (panel) panel.style.height = panel.scrollHeight + 'px';
    }

    this.lockFaqColumnHeights();
  }

  initFaqMobileToggle() {
    if (!this.faqRoot) return;

    this.faqToggleButton = this.faqRoot.querySelector('[data-faq-toggle]');
    this.faqMobileExpanded = false;
    this.FAQ_INITIAL_MOBILE_COUNT = 3;

    if (!this.faqToggleButton) return;

    if (!this.faqItems || this.faqItems.length <= this.FAQ_INITIAL_MOBILE_COUNT) {
      this.faqToggleButton.hidden = true;
      return;
    }

    this.faqToggleButton.addEventListener('click', () => {
      this.faqMobileExpanded = !this.faqMobileExpanded;
      this.applyFaqMobileVisibility(true);
    });

    this.applyFaqMobileVisibility(false);
  }

  isFaqMobile() {
    return window.innerWidth < 768;
  }

  applyFaqMobileVisibility(animated) {
    if (!this.faqItems || !this.faqToggleButton) return;

    const isMobile = this.isFaqMobile();

    if (!isMobile) {
      this.faqItems.forEach((item) => {
        item.classList.remove('is-hidden', 'is-entering', 'is-leaving', 'is-visible');
      });
      this.faqToggleButton.hidden = true;
      return;
    }

    this.faqToggleButton.hidden = false;
    this.faqToggleButton.textContent = this.faqMobileExpanded ? 'Show Less' : 'Load More';
    this.faqToggleButton.setAttribute('aria-expanded', String(this.faqMobileExpanded));

    this.faqItems.forEach((item, idx) => {
      const shouldShow = this.faqMobileExpanded || idx < this.FAQ_INITIAL_MOBILE_COUNT;

      if (animated) {
        if (shouldShow && item.classList.contains('is-hidden')) {
          this.showFaqItemAnimated(item);
        } else if (!shouldShow && !item.classList.contains('is-hidden')) {
          if (item === this.faqActiveItem) {
            this.closeFaqItem(item);
            this.faqActiveItem = null;
          }
          this.hideFaqItemAnimated(item);
        }
      } else {
        if (!shouldShow && item === this.faqActiveItem) {
          this.closeFaqItem(item);
          this.faqActiveItem = null;
        }
        item.classList.toggle('is-hidden', !shouldShow);
        item.classList.remove('is-entering', 'is-visible', 'is-leaving');
      }
    });
  }

  showFaqItemAnimated(item) {
    item.classList.remove('is-hidden', 'is-leaving');
    item.classList.add('is-entering');
    item.offsetHeight;
    requestAnimationFrame(() => {
      item.classList.remove('is-entering');
      item.classList.add('is-visible');
    });
  }

  hideFaqItemAnimated(item) {
    item.classList.remove('is-visible', 'is-entering');
    item.classList.add('is-leaving');

    const handleEnd = (e) => {
      if (e.propertyName !== 'opacity') return;
      if (!item.classList.contains('is-leaving')) return;
      item.classList.remove('is-leaving');
      item.classList.add('is-hidden');
      item.removeEventListener('transitionend', handleEnd);
    };

    item.addEventListener('transitionend', handleEnd);

    setTimeout(() => {
      if (item.classList.contains('is-leaving')) {
        item.classList.remove('is-leaving');
        item.classList.add('is-hidden');
      }
    }, 400);
  }

  initResizeHandler() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.lockVisibleAccordions();
        this.handleReviewsResize();
        this.applyFaqMobileVisibility(false);
        this.refreshProductTabsFaq();
      }, 150);
    });
  }

  initFontLoadHandler() {
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        requestAnimationFrame(() => {
          this.lockVisibleAccordions();
        });
      });
    }

    window.addEventListener('load', () => {
      requestAnimationFrame(() => {
        this.lockVisibleAccordions();
      });
    }, { once: true });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('[data-product-info-tabs]');
  sections.forEach(section => {
    new ProductInfoTabs(section);
  });
});
